import paper from "paper";
import type { StitchLine, Vec2 } from "./Project";
import { seamPathData, type SeamHandle } from "./seamGeometry";

/**
 * Cropping a stitch seam to the leather it overlaps (#780). Kept in its OWN
 * module — importing only `paper`, never the construction-offset engine — so the
 * move/transform paths (`SelectTool`, `pieceTransform`) can re-crop a seam
 * WITHOUT dragging `paperjs-offset` into their module graph (it instantiates a
 * real canvas at load, which the jsdom unit-test env can't provide).
 */

/** Target segment length (mm) when re-sampling a clipped run into a polyline —
 *  mirrors StitchTool's `SHAPE_SAMPLE_MM`. */
const SAMPLE_MM = 2;
/** Arc-length tolerance for treating a crossing as at a seam endpoint. */
const EPS_MM = 1e-4;
/** How far off the outline a point may sit and still count as on the leather
 *  (float noise on a crossing / boundary sample). */
const ON_OUTLINE_TOL_MM = 0.5;

/** Parse a part outline pathData into a CompoundPath (a lone Path is wrapped).
 *  Null when it won't parse. */
function parseOutline(
  pathData: string,
  scope: paper.PaperScope,
): paper.CompoundPath | null {
  try {
    return new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
}

/**
 * Clip a seam polyline to the inside of a part outline (#780). A single-edge
 * offset produces a parallel line whose ends overshoot the part's corners, so a
 * run of the seam lies OUTSIDE the leather; committing it verbatim lays holes
 * off the piece. This intersects the seam with `outlinePathData` and keeps only
 * the inside portion, re-sampled at `SAMPLE_MM` so hole layout then derives from
 * the clipped points.
 *
 * Fragmentation rule: if clipping splits the seam into several inside runs (a
 * concave part, a slot the seam skims across), the LONGEST inside run is kept —
 * a seam is a single polyline and the longest inside span is the one being
 * traced. A kept run is OPEN even when cut from a closed ring.
 *
 * A seam already wholly inside (the whole-outline closed inset, or a single-edge
 * inset that never reaches a corner) is returned unchanged — the SAME `points`
 * array by reference, so callers can detect a no-op. Null when nothing lands
 * inside; the original is returned untouched if the outline won't parse.
 */
export function clipSeamToOutline(
  points: Vec2[],
  closed: boolean,
  outlinePathData: string,
  scope: paper.PaperScope,
  /** The seam's bezier handles (#982). The clip walks the seam by ARC LENGTH,
   *  so without them a curved seam is measured and re-sampled along the chords
   *  between its anchors — holes land off the seam the user can see. */
  handles?: SeamHandle[],
): { points: Vec2[]; closed: boolean } | null {
  if (points.length < 2) return null;
  const outline = parseOutline(outlinePathData, scope);
  if (!outline) return { points, closed }; // outline unparseable → leave as-is

  const seam = new scope.Path({
    pathData: seamPathData({ points, handles, closed }),
    insert: false,
  });
  seam.closed = closed;
  const L = seam.length;
  if (!(L > 0)) {
    seam.remove();
    outline.remove();
    return null;
  }

  const onOrIn = (pt: paper.Point): boolean =>
    outline.contains(pt) ||
    (outline.getNearestLocation(pt)?.point.getDistance(pt) ?? Infinity) <=
      ON_OUTLINE_TOL_MM;

  // Crossing arc-length offsets along the seam (transversal boundary crossings
  // only), sorted + deduped, ignoring ones sitting on a seam endpoint.
  const cuts = Array.from(
    new Set(
      seam
        .getCrossings(outline)
        .map((c) => c.offset)
        .filter((o) => o > EPS_MM && o < L - EPS_MM),
    ),
  ).sort((a, b) => a - b);

  // No crossing → the whole seam is on one side. Keep it whole if inside.
  if (cuts.length === 0) {
    const mid = seam.getPointAt(L / 2);
    const inside = mid ? onOrIn(mid) : false;
    seam.remove();
    outline.remove();
    return inside ? { points, closed } : null;
  }

  // Intervals between consecutive cuts; for an open seam the two ends are also
  // interval boundaries, for a closed ring the last cut wraps to the first.
  const intervals: { from: number; to: number }[] = [];
  if (closed) {
    for (let i = 0; i < cuts.length; i++) {
      intervals.push({
        from: cuts[i]!,
        to: i + 1 < cuts.length ? cuts[i + 1]! : cuts[0]! + L,
      });
    }
  } else {
    const bounds = [0, ...cuts, L];
    for (let i = 0; i + 1 < bounds.length; i++)
      intervals.push({ from: bounds[i]!, to: bounds[i + 1]! });
  }

  // Keep the longest interval whose midpoint lies on the leather.
  let best: { from: number; to: number } | null = null;
  for (const iv of intervals) {
    const len = iv.to - iv.from;
    if (len < EPS_MM) continue;
    const midArc = (((iv.from + len / 2) % L) + L) % L;
    const midPt = seam.getPointAt(midArc);
    if (!midPt || !onOrIn(midPt)) continue;
    if (!best || len > best.to - best.from) best = iv;
  }
  if (!best) {
    seam.remove();
    outline.remove();
    return null;
  }

  // Re-sample the kept run at SAMPLE_MM, both crossing ends inclusive. Sample
  // ONLY within this one inside interval [from, to] and, for an OPEN seam, never
  // wrap the offset — a run reaching the seam's terminal end has `to === L`, and
  // `L % L` would fold back to offset 0 (the seam's OUTSIDE start point),
  // appending it and doubling the run back on itself (the #780 crop-on-move loop
  // bug: extra, bunched, uneven holes in one nudge direction). Only a CLOSED
  // ring's interval can legitimately run past L and needs the wrap.
  const runLen = best.to - best.from;
  const n = Math.max(1, Math.min(400, Math.round(runLen / SAMPLE_MM)));
  const out: Vec2[] = [];
  for (let i = 0; i <= n; i++) {
    const rawArc = best.from + (runLen * i) / n;
    const arc = closed
      ? ((rawArc % L) + L) % L
      : Math.min(Math.max(rawArc, 0), L);
    const pt = seam.getPointAt(arc);
    if (pt) out.push({ x: pt.x, y: pt.y });
  }
  seam.remove();
  outline.remove();
  return out.length >= 2 ? { points: out, closed: false } : null;
}

/** The seam geometry to store on a `StitchLine` after cropping the full authored
 *  seam to a part outline (#780): the cropped `points` (+ `closed`) plus, WHEN a
 *  crop actually removed geometry, the intact `authoredPoints` (+
 *  `authoredClosed`) so a later move can re-crop from the full seam. */
export interface CroppedSeam {
  points: Vec2[];
  closed: boolean;
  /** Handles for `points`. Undefined after a trim, which re-samples (#982). */
  handles?: SeamHandle[];
  authoredPoints?: Vec2[];
  authoredClosed?: boolean;
  /** Handles for `authoredPoints`, so a move back re-crops the real curve. */
  authoredHandles?: SeamHandle[];
}

/**
 * Crop a full authored seam to a part outline and report what to store on the
 * `StitchLine` (#780). Wraps {@link clipSeamToOutline}:
 *  - Seam wholly inside → no-op: `points` is the full seam, no `authoredPoints`.
 *  - Seam overshoots → `points` is the trimmed run and the full seam is kept as
 *    `authoredPoints` (with its own `authoredClosed`) for reversible re-crop.
 *  - Seam wholly OUTSIDE (a move dragged it clear of the part) → keep the full
 *    seam as both `points` and `authoredPoints`, so moving back re-crops. This
 *    is the only case a hole can sit off the leather, and it's transient +
 *    reversible.
 * `fullPoints` / `fullClosed` describe the authored seam; the outline is in the
 * SAME coordinate space as the points.
 */
export function cropSeamForPiece(
  fullPoints: Vec2[],
  fullClosed: boolean,
  outlinePathData: string,
  scope: paper.PaperScope,
  fullHandles?: SeamHandle[],
): CroppedSeam {
  const clip = clipSeamToOutline(
    fullPoints,
    fullClosed,
    outlinePathData,
    scope,
    fullHandles,
  );
  if (!clip) {
    // Nothing lands inside — retain the full seam so a move back re-crops.
    return {
      points: fullPoints,
      closed: fullClosed,
      handles: fullHandles,
      authoredPoints: fullPoints,
      authoredClosed: fullClosed,
      authoredHandles: fullHandles,
    };
  }
  // `clipSeamToOutline` returns the SAME array on a no-op (wholly inside); a
  // different array means it trimmed geometry we must preserve as authored.
  if (clip.points === fullPoints)
    return { points: fullPoints, closed: fullClosed, handles: fullHandles };
  return {
    points: clip.points,
    closed: clip.closed,
    // A trimmed run is RE-SAMPLED along the curve into a dense point list, so
    // the authored handles no longer index anything (#982). Dropping them is
    // what keeps the two lists aligned — kept, they'd bend runs between points
    // they were never measured against. The curve itself is preserved in the
    // sampling, and in `authoredHandles` for the re-crop on a move back.
    handles: undefined,
    authoredPoints: fullPoints,
    authoredClosed: fullClosed,
    authoredHandles: fullHandles,
  };
}

/** Whether a seam should be cropped to its part at all (#780). Construction-
 *  derived seams already clip per-part (#199), and matched seams (#199 stitch
 *  matching) intentionally span parts — neither should be trimmed. */
export function isCroppableSeam(line: StitchLine): boolean {
  return (
    line.constructionOutline == null &&
    line.matchId == null &&
    line.holeCount == null &&
    line.gridPhase == null
  );
}

/**
 * Translate a seam by `(dx, dy)` and re-derive its crop to the part (#780). The
 * full authored geometry (`authoredPoints`, else the current `points` when the
 * seam has never been trimmed) moves rigidly, then it is re-cropped to
 * `outlinePathData` at the new position — so moving a seam so it overlaps the
 * leather at DIFFERENT points than at creation drops holes that left the part
 * and restores holes that came back. Non-croppable seams (construction-derived,
 * matched) just translate, carrying any `authoredPoints` along. The outline must
 * be in the same coordinate space as the stored points (a part's own
 * `shapePathData`, since stored seams live in the part's original space).
 */
export function translateAndCropStitchLine(
  line: StitchLine,
  dx: number,
  dy: number,
  outlinePathData: string,
  scope: paper.PaperScope,
): StitchLine {
  const shift = (pts: Vec2[]) => pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  if (!isCroppableSeam(line)) {
    const moved: StitchLine = { ...line, points: shift(line.points) };
    if (line.authoredPoints) moved.authoredPoints = shift(line.authoredPoints);
    return moved;
  }
  const fullMoved = shift(line.authoredPoints ?? line.points);
  const fullClosed = line.authoredPoints
    ? (line.authoredClosed ?? false)
    : line.closed === true;
  // Handles are RELATIVE, so the rigid move leaves them untouched (#982).
  const fullHandles = line.authoredPoints ? line.authoredHandles : line.handles;
  const seam = cropSeamForPiece(
    fullMoved,
    fullClosed,
    outlinePathData,
    scope,
    fullHandles,
  );
  return {
    ...line,
    points: seam.points,
    handles: seam.handles,
    closed: (seam.closed && seam.points.length >= 3) || undefined,
    authoredPoints: seam.authoredPoints,
    authoredClosed: seam.authoredClosed,
    authoredHandles: seam.authoredHandles,
  };
}
